import React, { useState, useMemo, useCallback } from "react";
import AppDataGrid from "../../common/AppDataGrid";
import {
  CircularProgress,
  Box,
  Container,
  Snackbar,
  Alert,
} from "@mui/material";
import useFirestore from "../../../config/fireBaseHook";
import Add from "./inc/Add";
import Edit from "./inc/Edit";
import EditUpload from "../../../hooks/EditUpload";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import { useAuth } from "../../../config/AuthContext";
import SimpleToolbar from "../../common/SimpleToolbar";
import CustomFilterPanel from "../../common/CustomFilterPanel";
import useCustomFilter from "../../../hooks/useCustomFilter";
import { partnersFilters } from "../filterConfigurations";
import { formatDateTime } from "../../../utils/formatDate";

const Partners = () => {
  const { userPermissions } = useAuth();
  const canEdit = userPermissions["Partners"]?.edit;

  // Use Firestore hook, order by Tech_Prep_Partner ascending. Collection name "TpPartners"
  const {
    data: partners,
    loading,
    error,
  } = useFirestore("TpPartners", "Tech_Prep_Partner", "asc");
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [sortModel, setSortModel] = useState([]);
  const [paginationModel, setPaginationModel] = useState({
    pageSize: 100,
    page: 0,
  });

  // Custom filter implementation
  const {
    filteredData,
    filterModel,
    setFilterModel,
    resetFilters,
    hasActiveFilters,
    isFiltering,
  } = useCustomFilter(partners || [], partnersFilters);

  const pageName = "Partners";

  const handleRowClick = (params) => {
    if (canEdit) {
      setSelectedPartner(params.row);
      setEditDialogOpen(true);
    }
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedPartner(null);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleRecordUpdated = () => {
    setSnackbarMessage("Record has been updated successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
    setEditDialogOpen(false);
    // Data will refresh automatically due to onSnapshot in useFirestore
  };

  const handleRecordAdded = () => {
    // Data will refresh automatically due to onSnapshot in useFirestore
    setSnackbarMessage("Record has been added successfully!");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);
  };

  const handleBulkActionComplete = (success, message) => {
    setSnackbarMessage(
      message ||
        (success ? "Bulk operation successful." : "Bulk operation failed."),
    );
    setSnackbarSeverity(success ? "success" : "error");
    setSnackbarOpen(true);
    // Data should refresh automatically via useFirestore's onSnapshot.
  };

  // Helper function to get display string for the Active_yn field ("y"/"n")
  const getBooleanDisplayValue = (value) => {
    if (value === null || typeof value === "undefined" || value === "") {
      return "";
    }
    const normalized = String(value).trim().toLowerCase();
    if (normalized === "y" || normalized === "yes") {
      return "Yes";
    }
    if (normalized === "n" || normalized === "no") {
      return "No";
    }
    return String(value);
  };

  const columns = useMemo(
    () => [
      {
        field: "PartnerIRN",
        headerName: "Partner IRN",
        flex: 1,
        minWidth: 130,
      },
      {
        field: "Tech_Prep_Partner",
        headerName: "Tech Prep Partner",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "Partner_Full_Name",
        headerName: "Full Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "Active_yn",
        headerName: "Active",
        flex: 1,
        minWidth: 100,
        renderCell: (params) => getBooleanDisplayValue(params.value),
      },
      {
        field: "Sites",
        headerName: "Sites",
        flex: 1.5,
        minWidth: 220,
        sortable: false,
        renderCell: (params) => {
          const sites = params.value;
          if (!Array.isArray(sites) || sites.length === 0) return "";
          return sites
            .map((site) => site.SiteName)
            .filter(Boolean)
            .join(", ");
        },
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => formatDateTime(params.value),
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        flex: 1,
        minWidth: 160,
        renderCell: (params) => formatDateTime(params.value),
      },
    ],
    [],
  );

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    if (!filteredData || !Array.isArray(filteredData)) return [];
    return filteredData.map((partner, index) => ({
      id: partner.id || index, // Use partner.id if available, otherwise use index
      ...partner,
    }));
  }, [filteredData]);

  // Simple toolbar without search (search is external)
  const CustomToolbar = useCallback(() => {
    return (
      <SimpleToolbar
        exportOptions={{
          csvOptions: {
            fileName: "partners",
            delimiter: ",",
          },
          printOptions: {
            hideFooter: true,
            hideToolbar: true,
          },
        }}
      />
    );
  }, []);

  const exportColumnOrder = columns.map((col) => col.field);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <p>
          Error fetching partners:{" "}
          {typeof error === "string"
            ? error
            : error?.message || "Unknown error"}
        </p>
      </Box>
    );
  }

  return (
    <Container maxWidth={false} sx={{ height: "100vh", paddingTop: "50px" }}>
      <div style={{ height: "100%", width: "100%" }}>
        <TechPrepPortalMgtBreadCrumbs pageName={pageName} icon="faCog" />

        <CustomFilterPanel
          filterConfig={partnersFilters}
          onFiltersChange={setFilterModel}
          initialFilters={filterModel}
          collapsible={true}
          title="Partners Filters"
          resultCount={filteredData?.length || 0}
          totalCount={partners?.length || 0}
          isSearching={isFiltering}
        />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between", // Adjust as needed for layout
            alignItems: "center",
            mb: 2,
            flexWrap: "wrap", // Allow wrapping on smaller screens
          }}
        >
          {canEdit && <Add onRecordAdded={handleRecordAdded} />}
          {canEdit && (
            <EditUpload
              collectionName="TpPartners"
              onActionComplete={handleBulkActionComplete}
              exportColumnOrder={columns.map((col) => col.field)}
              exportData={filteredData}
              primaryKeyField="PartnerIRN"
              booleanFields={["Active_yn"]}
              templateFields={exportColumnOrder}
            />
          )}
          {canEdit && <ClearCollectionButton collectionName="TpPartners" />}
        </Box>

        <Box sx={{ height: "70vh", width: "100%" }}>
          <AppDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            slots={{ toolbar: CustomToolbar }}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[25, 50, 100, 200]}
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            sx={{
              "& .MuiDataGrid-row:hover": {
                cursor: canEdit ? "pointer" : "default",
              },
            }}
          />
        </Box>
      </div>
      {selectedPartner && canEdit && (
        <Edit
          open={editDialogOpen}
          onClose={handleDialogClose}
          partnerDetail={selectedPartner}
          onRecordUpdated={handleRecordUpdated}
        />
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Partners;
